// Copyright ©️ 2026 eVolve MEP, LLC
import { useState } from 'react';

import { Flex, Text, Group, Stack, Button, SegmentedControl, Box } from '@mantine/core';

import type { BendTypeName } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useOptionalBendCreatorCartContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/Context/useBendCreatorCartContext';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/Context/useBendCreatorFormContext';

import type { BendPreviewSpecifications } from '../BendPreview/BendPreviewGeometry';
import type { RenderMode } from '../BendPreview/BendPreviewRenderer';
import { BendRenderArea } from '../BendPreview/BendRenderArea';
import type { BendCreatorStep } from './Navigation/BendCreatorStepPanel';
import { CreatorBendList } from './Navigation/CreatorBendList';
import { CreatorSpecsNavigationPanel } from './Navigation/CreatorSpecsNavigationPanel';
import { useValidatedPreviewData } from './useValidatedPreviewData';

const renderModeOptions = [
  { value: '3d', label: 'Iso' },
  { value: '2dFront', label: 'Front' },
  { value: '2dTop', label: 'Top' },
] as const satisfies { value: RenderMode; label: string }[];

type Props = {
  /**
   * A bend type to display in the preview area when no bend is selected.
   * @defaultValue "Stub 90"
   */
  defaultBendType?: BendTypeName;
  /**
   * A callback to close the bend creator form
   */
  onClose: () => void;
  isEditing?: boolean;
};

const FloatingFormAreas = ({
  bendPreviewData,
  isEditing,
}: {
  bendPreviewData: BendPreviewSpecifications | null;
  isEditing?: boolean;
}) => {
  const cartContext = useOptionalBendCreatorCartContext();
  const [step, setStep] = useState<BendCreatorStep | null>(null);
  return (
    <Stack p="md" pos="absolute" h="100%" w="100%" style={{ zIndex: 1, pointerEvents: 'none' }} justify="space-between">
      {/* Bounded height, so the step panel scrolls instead of running off the screen. */}
      <Group left={0} top={0} align="start" justify="space-between" flex={1} mih={0}>
        <Box style={{ pointerEvents: 'auto', flexDirection: 'column' }} w={375} mah="100%" display="flex">
          <CreatorSpecsNavigationPanel isEditing={isEditing} currentStep={step} onChangeStep={setStep} />
        </Box>
        <Box style={{ pointerEvents: 'auto' }} w={300}>
          {isNotNil(cartContext) && <CreatorBendList step={step} onChangeStep={setStep} />}
        </Box>
      </Group>
      {isNil(bendPreviewData) && (
        <Text size="xs" c="dimmed" fs="italic" maw={500}>
          This is a preview of the bend type. A preview using your specified dimensions will be generated once you have
          entered valid values.
        </Text>
      )}
    </Stack>
  );
};

const RenderControlsSection = ({
  renderMode,
  onChangeRenderMode,
  onClose,
}: {
  renderMode: RenderMode;
  onChangeRenderMode: (mode: RenderMode) => void;
  onClose: () => void;
}) => (
  <Group p="md" pos="absolute" justify="center" w="100%" style={{ zIndex: 1, pointerEvents: 'none' }}>
    <Group style={{ pointerEvents: 'auto' }}>
      <SegmentedControl
        data={renderModeOptions}
        value={renderMode}
        onChange={onChangeRenderMode}
        styles={{ label: { fontWeight: 'normal', minWidth: '85px' } }}
      />
      <Button onClick={onClose} variant="light">
        Exit
      </Button>
    </Group>
  </Group>
);

export const BendCreatorFormContents = ({ defaultBendType = 'Stub 90', onClose, isEditing }: Props) => {
  const [renderMode, setRenderMode] = useState<RenderMode>('3d');
  const { values } = useBendCreatorFormContext();
  const previewBendType = values.bendType ?? defaultBendType;

  const bendPreviewData = useValidatedPreviewData(previewBendType);

  return (
    <>
      <FloatingFormAreas bendPreviewData={bendPreviewData} isEditing={isEditing} />
      <RenderControlsSection renderMode={renderMode} onChangeRenderMode={setRenderMode} onClose={onClose} />
      <Flex pos="relative" flex={1} h="100%">
        <BendRenderArea
          renderMode={renderMode}
          bendType={previewBendType}
          bendPreviewData={bendPreviewData}
          xMargin={400}
          yMargin={75}
        />
      </Flex>
    </>
  );
};
